import Navbar from "@/components/Navbar";
import InquiryForm from "@/components/InquiryForm";
import QuoteCalculator from "@/components/QuoteCalculator";
import CoastMap from "@/components/CoastMap";
import BringYourOwnBooze from "@/components/BringYourOwnBooze";
import { SunMotif } from "@/components/GlobalSvgDefs";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, TreePine, Tent, Compass } from "lucide-react";

const TRAILER_NIGHT = "https://customer-assets.emergentagent.com/job_trailhead-reserve/artifacts/jxd8o2ws_615A0338-268D-4724-8809-246BC7FFCED2.JPG";
const TRAILER_DAY = "https://customer-assets.emergentagent.com/job_trailhead-reserve/artifacts/lfm8vmhd_IMG_6044.jpg";
const TRAILER_OPEN = "https://customer-assets.emergentagent.com/job_trailhead-reserve/artifacts/7vlbrnkr_IMG_6052.jpg";
const KEG_DETAIL = "https://customer-assets.emergentagent.com/job_trailhead-reserve/artifacts/z42de3u0_IMG_6054.jpg";
const SPEC_DIAGRAM = "https://customer-assets.emergentagent.com/job_trailhead-reserve/artifacts/hdlq65mp_ChatGPT%20Image%20May%2012%2C%202026%2C%2012_41_26%20PM%20%281%29.PNG";

export default function LandingPage() {
  return (
    <div className="min-h-screen cream-bg overflow-hidden">
      <Navbar />

      {/* MARQUEE */}
      <div className="bg-[#1f2e1f] text-[#e8dcc4] overflow-hidden border-b border-[#3a2c22]">
        <div className="flex whitespace-nowrap animate-marquee py-2 text-xs uppercase tracking-[0.35em]">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex shrink-0 items-center gap-12 px-6">
              <span>★ Long Beach, CA</span>
              <span className="text-[#f5b85c]">Up to 4 taps</span>
              <span>Hand-built cedar</span>
              <span className="text-[#f5b85c]">Pacific Coast vibes</span>
              <span>Weddings · Backyards · Festivals</span>
              <span className="text-[#f5b85c]">Sun-soaked · Salt-rinsed · SoCal</span>
              <span>Pour, repeat, party</span>
            </div>
          ))}
        </div>
      </div>

      {/* HERO — editorial split */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-6 relative z-10">
              <div className="flex items-center gap-3 fade-up fade-up-1" data-testid="hero-eyebrow">
                <span className="h-px w-12 bg-[#c97b3a]" />
                <span className="text-xs font-semibold uppercase tracking-[0.4em] text-[#c97b3a]">A SoCal Pour Trailer</span>
              </div>
              <h1 className="font-display text-[64px] sm:text-[84px] lg:text-[112px] leading-[0.85] tracking-[-0.04em] text-[#1f2e1f] mt-5 fade-up fade-up-2" data-testid="hero-headline">
                Where<br />
                <span className="italic font-normal text-[#c97b3a]">cedar</span><br />
                meets the<br />
                <span className="italic font-normal">coast.</span>
              </h1>
              <p className="mt-7 text-lg text-[#5a5245] max-w-md leading-relaxed fade-up fade-up-3" data-testid="hero-subhead">
                Trailhead Taps is a hand-built cedar tap trailer based in Long Beach. Up to four lines, big personality, towed wherever your party is — backyard, beach, brewery, or the middle of nowhere.
              </p>
              <div className="mt-8 flex flex-wrap gap-3 fade-up fade-up-3">
                <Button asChild className="btn-orange rounded-none px-7 py-6 text-base font-semibold" data-testid="hero-cta-book">
                  <a href="#inquiry">Reserve the trailer <ArrowRight className="ml-2 h-4 w-4" /></a>
                </Button>
                <Button asChild variant="outline" className="rounded-none px-7 py-6 text-base border-2 border-[#1f2e1f] text-[#1f2e1f] hover:bg-[#1f2e1f] hover:text-white" data-testid="hero-cta-how">
                  <a href="#the-rig">See the rig</a>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-6 relative">
              <SunMotif tone="#f5b85c" size={520} className="absolute -top-10 -right-6 opacity-25 pointer-events-none z-0" />
              <div className="relative aspect-[4/5] w-full max-w-[520px] mx-auto z-10">
                <div className="absolute inset-0 -rotate-2 border-[8px] border-[#1f2e1f] bg-[#1f2e1f]" />
                <img src={TRAILER_NIGHT} alt="Trailhead Taps trailer at night with fairy lights" className="absolute inset-0 w-full h-full object-cover rotate-1 fade-up fade-up-2 shadow-2xl clip-art" />
                <div className="absolute -bottom-6 -left-6 bg-[#F8F5F0] border-4 border-[#1f2e1f] px-5 py-3 rotate-[-3deg] z-10 fade-up fade-up-3">
                  <div className="font-display text-2xl text-[#1f2e1f] leading-none italic">"Welcome to<br/>our adventure"</div>
                </div>
                <div className="absolute -top-5 -right-4 bg-[#c97b3a] text-white px-4 py-2 rotate-[6deg] z-10 fade-up fade-up-3 shadow-xl">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold">Long Beach</div>
                  <div className="font-display text-2xl leading-none italic">CA</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden pointer-events-none select-none">
          <h2 aria-hidden className="font-display italic font-normal text-[20vw] leading-[0.8] text-[#1f2e1f]/[0.06] whitespace-nowrap -ml-4">
            cheers · cheers · cheers
          </h2>
        </div>
      </section>

      {/* 001 — THE RIG (about us) */}
      <section id="the-rig" className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16 scroll-mt-32">
        <div className="grid md:grid-cols-12 gap-6 items-end mb-10">
          <div className="md:col-span-8">
            <span className="text-xs font-semibold uppercase tracking-[0.35em] text-[#c97b3a]">001 — The Rig</span>
            <h2 className="font-display text-5xl md:text-7xl text-[#1f2e1f] leading-[0.9] mt-3">
              Hand-cut cedar.<br />
              <span className="italic font-normal">Hand-tuned</span> taps.<br />
              <span className="italic text-[#c97b3a]">One of one.</span>
            </h2>
          </div>
          <p className="md:col-span-4 text-[#5a5245] leading-relaxed">
            Built by hand from cedar boards and matte black trim — there's only one of her. Inside: an insulated cold-box, glycol-cooled lines, regulators, and a kegerator setup that keeps the foam down and the line clean, from Long Beach BBQs to Joshua Tree weddings.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          <div className="col-span-12 md:col-span-7 relative group">
            <img src={TRAILER_OPEN} alt="Open trailer view" className="w-full aspect-[4/3] object-cover clip-art" />
            <div className="absolute bottom-4 left-4 bg-[#F8F5F0] px-3 py-1.5">
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#1f2e1f] font-semibold">Daylight pours</span>
            </div>
          </div>
          <div className="col-span-12 md:col-span-5 relative">
            <img src={KEG_DETAIL} alt="Keg system" className="w-full h-full object-cover clip-art" />
            <div className="absolute top-3 right-3 bg-[#c97b3a] text-white text-[10px] uppercase tracking-[0.3em] px-2.5 py-1 font-bold">Inside</div>
          </div>
          <div className="col-span-12 md:col-span-7 relative">
            <img src={TRAILER_DAY} alt="Trailer with cups" className="w-full aspect-[16/9] object-cover clip-art" />
          </div>
          <div className="col-span-12 md:col-span-5 relative bg-white border border-[#c9bfa6] p-6 flex items-center justify-center">
            <img src={SPEC_DIAGRAM} alt="Spec diagram" className="w-full object-contain max-h-64 clip-art" />
          </div>
        </div>
      </section>

      {/* 002 — HOW IT GOES DOWN (the process) */}
      <section id="how-it-works" className="bg-[#1f2e1f] text-[#e8dcc4] border-t border-b border-[#3a2c22] scroll-mt-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.35em] text-[#c97b3a]">002 — How it goes down</span>
              <h2 className="font-display text-5xl md:text-7xl text-[#F8F5F0] leading-[0.9] mt-3">
                Three steps.<br />
                <span className="italic">Then you party.</span>
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", icon: Compass, t: "Tell us the vibe", d: "Fill out the inquiry form or chat with our AI bartender below. We'll send a quote within 24 hours." },
              { n: "02", icon: TreePine, t: "Pick your pours", d: "Beer, wine, cocktails, kombucha — your call, up to 4 taps. We help size kegs and connect with local LB breweries." },
              { n: "03", icon: Tent, t: "We roll up, you celebrate", d: "We tow in, set up in about 45 minutes, and even string the fairy lights (decoration package). Add a bartender or DIY." },
            ].map((s, i) => (
              <div key={i} data-testid={`feature-card-${i}`} className="bg-[#243024] border border-[#e8dcc4]/15 p-7 hover:-translate-y-1 transition-transform duration-300 group">
                <div className="flex items-start justify-between mb-6">
                  <span className="font-display text-7xl text-[#e8dcc4]/10 leading-none">{s.n}</span>
                  <div className="h-11 w-11 rounded-full bg-[#c97b3a]/15 flex items-center justify-center group-hover:bg-[#c97b3a] transition-colors">
                    <s.icon className="h-5 w-5 text-[#c97b3a] group-hover:text-white" />
                  </div>
                </div>
                <h3 className="font-display text-2xl text-[#F8F5F0] mb-2">{s.t}</h3>
                <p className="text-sm text-[#e8dcc4]/70 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 003 — BUILD YOUR BALLPARK */}
      <QuoteCalculator />

      {/* 004 — BRING YOUR OWN BOOZE */}
      <BringYourOwnBooze />

      {/* 005 — FROM THE COAST TO THE CANYONS */}
      <CoastMap />

      {/* 006 — LOCK IN THE DATE */}
      <section id="inquiry" className="relative py-12 md:py-16 bg-[#1f2e1f] text-[#F8F5F0] scroll-mt-32">
        <div className="absolute inset-0 -z-0 opacity-25">
          <img src={TRAILER_NIGHT} alt="" className="w-full h-full object-cover clip-art" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-[0.35em] text-[#c97b3a]">006 — Lock in the date</span>
            <h2 className="font-display text-5xl md:text-7xl mt-3 leading-[0.9]">
              Let's get you<br />
              <span className="italic text-[#c97b3a]">on the calendar.</span>
            </h2>
            <p className="mt-5 max-w-xl mx-auto text-[#e8dcc4]/80">A few quick details and we'll be back with a quote within 24 hours. Or chat with our AI bartender in the corner.</p>
          </div>
          <InquiryForm />
        </div>
      </section>

      {/* 007 — FAQ */}
      <section id="faq" className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16 scroll-mt-32">
        <div className="mb-8">
          <span className="text-xs font-semibold uppercase tracking-[0.35em] text-[#c97b3a]">007 — FAQ</span>
          <h2 className="font-display text-5xl md:text-6xl text-[#1f2e1f] mt-3 leading-[0.9]">
            Common <span className="italic">curiosities.</span>
          </h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {[
            { q: "How much does it cost?", a: "Our floor rate is $500 for a 5-hour rental with 1 tap and a small headcount (up to ~25 guests). From there it scales gently with extra taps (+$50 each, max 4), longer hours (+$75/hr above 5), guest count, beverage type, and add-ons (bartender +$150, custom decoration +$150). Delivery within 25 mi of Long Beach is free." },
            { q: "Do you supply the drinks?", a: "No — California licensing means clients provide their own kegs/wine/spirits. We're happy to help you size quantities and we have a BYO guide on this page with links to BevMo, Total Wine, and local Long Beach breweries." },
            { q: "How far do you travel?", a: "We're Long Beach-based and serve LA, OC, the Inland Empire, Ventura, and San Diego. Free within 25 mi, $2/mile beyond. We do not serve Catalina Island." },
            { q: "How early should I book?", a: "Lock in 4–8 weeks ahead for weekends. A 50% deposit reserves your date." },
            { q: "What space do you need on-site?", a: "About 12×10 ft of flat ground with vehicle access. Setup takes ~45 minutes." },
            { q: "Can we get the fairy lights / decoration?", a: "Yes — we offer a custom decoration package (fairy lights, faux ivy, signage, custom touches) as a $150 add-on. We install everything ourselves; for safety and consistency we don't allow clients to decorate the trailer." },
          ].map((item, i) => (
            <AccordionItem value={`q-${i}`} key={i} className="bg-white border border-[#c9bfa6] px-5">
              <AccordionTrigger data-testid={`faq-q-${i}`} className="font-display text-lg text-[#1f2e1f] hover:no-underline text-left">{item.q}</AccordionTrigger>
              <AccordionContent className="text-[#5a5245] leading-relaxed">{item.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FOOTER */}
      <footer className="wood-bg text-[#e8dcc4]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <div className="grid md:grid-cols-12 gap-8 mb-8">
            <div className="md:col-span-6">
              <div className="font-display text-5xl md:text-6xl text-[#F8F5F0] leading-none">Trailhead<br/><span className="italic text-[#c97b3a]">Taps.</span></div>
              <p className="text-sm mt-4 text-[#e8dcc4]/70 max-w-sm leading-relaxed">Hand-built mobile tap trailer based in Long Beach, California. Pouring at weddings, backyards, and beachfronts from the canyons to the coast.</p>
            </div>
            <div className="md:col-span-3">
              <div className="text-xs uppercase tracking-[0.3em] text-[#c97b3a] mb-3">Find Us</div>
              <p className="text-sm">mvargas@trailheadtaps.com</p>
              <p className="text-sm">Long Beach, CA</p>
              <p className="text-sm text-[#e8dcc4]/60 mt-2">Serving LA · OC · IE · SD</p>
            </div>
            <div className="md:col-span-3">
              <div className="text-xs uppercase tracking-[0.3em] text-[#c97b3a] mb-3">Navigate</div>
              <div className="flex flex-col gap-2 text-sm">
                <a href="#the-rig" className="hover:text-[#c97b3a] transition-colors">The Rig</a>
                <a href="#inquiry" className="hover:text-[#c97b3a] transition-colors">Reserve</a>
                <a href="/dashboard" className="hover:text-[#c97b3a] transition-colors">Admin</a>
              </div>
            </div>
          </div>
          <div className="border-t border-[#e8dcc4]/10 pt-6 flex flex-wrap items-center justify-between gap-3 text-xs text-[#e8dcc4]/60">
            <span>© {new Date().getFullYear()} Trailhead Taps. Pour on.</span>
            <span className="italic font-display text-base text-[#c97b3a] flex items-center gap-2"><TreePine className="h-4 w-4" />"Welcome to our adventure."</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
